import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_LEGAL_SETTINGS, LegalSettings } from '../services/queries';
import { BookOpen, FileText, LogIn, AlertTriangle, Users, CreditCard, Upload, Award, ShieldOff, RefreshCw, Scale, ChevronDown, ChevronUp, ScrollText } from 'lucide-react';

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const TermsOfServicePage: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>(['1']);
  const { data } = useQuery<{ legalSettings: LegalSettings }>(GET_LEGAL_SETTINGS);
  const legal = data?.legalSettings;

  const toggleSection = (id: string) => {
    setExpandedSections(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const sections: Section[] = [
    {
      id: '1',
      title: '1. Premesse e definizioni',
      icon: <BookOpen className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>I presenti Termini di Servizio regolano l'utilizzo della piattaforma studi.unasca.it. Accedendo alla Piattaforma, l'Utente accetta i presenti Termini.</p>
          <ul className="space-y-2">
            <li><strong>"Piattaforma"</strong>: il sito web studi.unasca.it e tutti i servizi ad esso collegati.</li>
            <li><strong>"Gestore"</strong>: {legal?.legalEntityName || '[Da configurare in Impostazioni]'}, con sede in {legal?.legalEntityAddress || '[Indirizzo]'}.</li>
            <li><strong>"Utente"</strong>: chiunque acceda alla Piattaforma.</li>
            <li><strong>"Associato"</strong>: soggetto iscritto a UNASCA che accede all'Area Riservata.</li>
            <li><strong>"Area Riservata"</strong>: sezione della Piattaforma accessibile previa autenticazione.</li>
            <li><strong>"Community"</strong>: spazio di discussione riservato ai membri della Rete Certificata.</li>
          </ul>
        </div>
      ),
    },
    {
      id: '2',
      title: '2. Oggetto del servizio',
      icon: <FileText className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>La Piattaforma offre i seguenti servizi:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Sezione pubblica:</strong> informazioni istituzionali, blog, gallerie fotografiche, mappa della Rete Certificata.</li>
            <li><strong>Area Riservata Associati:</strong> dashboard personale, profilo, strumenti associativi, documenti, sondaggi, feedback.</li>
            <li><strong>Community:</strong> forum di discussione e messaggi diretti, riservati ai membri della Rete Certificata.</li>
            <li><strong>Pagamenti online:</strong> rinnovo della certificazione Rete Certificata tramite il circuito Stripe.</li>
          </ul>
        </div>
      ),
    },
    {
      id: '3',
      title: '3. Registrazione e accesso',
      icon: <LogIn className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <ul className="list-disc list-inside space-y-2">
            <li>L'accesso all'Area Riservata avviene tramite autenticazione mediante codice OTP (One Time Password) inviato all'indirizzo email associato al proprio account.</li>
            <li>L'Utente è responsabile della riservatezza del proprio accesso email e di tutti gli accessi effettuati tramite il proprio account.</li>
            <li>L'accesso è strettamente personale e non cedibile a terzi.</li>
            <li>Il Gestore si riserva il diritto di sospendere o revocare l'accesso in caso di utilizzo improprio.</li>
          </ul>
        </div>
      ),
    },
    {
      id: '4',
      title: '4. Obblighi dell\'utente',
      icon: <AlertTriangle className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>L'Utente si impegna a:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>Fornire dati veritieri, aggiornati e completi.</li>
            <li>Non utilizzare la Piattaforma per finalità illecite o contrarie all'ordine pubblico e al buon costume.</li>
            <li>Non tentare di accedere a sezioni della Piattaforma per le quali non dispone dell'autorizzazione.</li>
            <li>Non caricare contenuti offensivi, diffamatori, illegali o in violazione di diritti di terzi.</li>
            <li>Non interferire con il corretto funzionamento della Piattaforma mediante attacchi informatici, tentativi di accesso non autorizzato o altre attività dannose.</li>
            <li>Segnalare tempestivamente al Gestore qualsiasi vulnerabilità o problema di sicurezza riscontrato.</li>
          </ul>
        </div>
      ),
    },
    {
      id: '5',
      title: '5. Regole della Community',
      icon: <Users className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <ul className="list-disc list-inside space-y-2">
            <li>La Community è riservata ai membri della Rete Certificata UNASCA.</li>
            <li>I contenuti pubblicati (post, commenti, risposte) sono visibili agli altri membri della Community.</li>
            <li>Sono vietati: spam, contenuti offensivi o discriminatori, pubblicità non autorizzata, pubblicazione di dati personali di terzi senza il loro consenso.</li>
            <li>Il Gestore si riserva il diritto di moderare, nascondere o rimuovere contenuti in violazione delle presenti regole, senza preavviso.</li>
            <li>La reiterata violazione delle regole della Community può comportare la sospensione temporanea o definitiva dall'accesso alla stessa.</li>
          </ul>
        </div>
      ),
    },
    {
      id: '6',
      title: '6. Pagamenti',
      icon: <CreditCard className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <ul className="list-disc list-inside space-y-2">
            <li>I pagamenti relativi alla Rete Certificata sono elaborati tramite la piattaforma Stripe Inc., in conformità ai più elevati standard di sicurezza (PCI DSS).</li>
            <li>Il Gestore non conserva né ha accesso ai dati delle carte di credito degli Utenti.</li>
            <li>Le ricevute di pagamento sono scaricabili dall'Area Riservata della Piattaforma.</li>
            <li>Il pagamento è effettuabile anche tramite bonifico bancario, secondo le modalità indicate nella piattaforma.</li>
            <li>Per rimborsi o contestazioni relative ai pagamenti, l'Utente può contattare il Gestore all'indirizzo{' '}
              {legal?.legalEntityEmail ? (
                <a href={`mailto:${legal.legalEntityEmail}`} className="text-primary-600 dark:text-primary-400 hover:underline">{legal.legalEntityEmail}</a>
              ) : '[Da configurare]'}.
            </li>
          </ul>
        </div>
      ),
    },
    {
      id: '7',
      title: '7. Upload e contenuti dell\'utente',
      icon: <Upload className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <ul className="list-disc list-inside space-y-2">
            <li>L'Utente può caricare documenti (PDF, immagini) nei limiti previsti dalla Piattaforma.</li>
            <li>I file caricati sono accessibili esclusivamente al personale autorizzato del Gestore e all'Utente stesso.</li>
            <li>Il Gestore si riserva il diritto di rimuovere file che violino i presenti Termini o la normativa vigente.</li>
            <li>I documenti relativi agli atti di svincolo vengono eliminati automaticamente dopo il periodo previsto dalla normativa applicabile.</li>
            <li>L'Utente garantisce di avere i diritti necessari sui contenuti caricati e manleva il Gestore da qualsiasi pretesa di terzi.</li>
          </ul>
        </div>
      ),
    },
    {
      id: '8',
      title: '8. Proprietà intellettuale',
      icon: <Award className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <ul className="list-disc list-inside space-y-2">
            <li>Tutti i contenuti della Piattaforma (testi, immagini, loghi, grafica, software) sono di proprietà di UNASCA o dei rispettivi titolari e sono protetti dalle leggi sul diritto d'autore e sulla proprietà industriale.</li>
            <li>È vietata la riproduzione, distribuzione, modifica o utilizzo commerciale dei contenuti senza preventiva autorizzazione scritta del Gestore.</li>
            <li>L'Utente mantiene la titolarità dei contenuti da lui caricati, concedendo al Gestore una licenza non esclusiva, gratuita e limitata alla visualizzazione e gestione degli stessi nell'ambito della Piattaforma.</li>
          </ul>
        </div>
      ),
    },
    {
      id: '9',
      title: '9. Limitazione di responsabilità',
      icon: <ShieldOff className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <ul className="list-disc list-inside space-y-2">
            <li>Il Gestore si impegna a garantire la continuità del servizio ma non garantisce l'assenza di interruzioni, errori o malfunzionamenti.</li>
            <li>Il Gestore non è responsabile per danni diretti o indiretti derivanti da interruzioni, malfunzionamenti, attacchi informatici, perdita di dati o cause di forza maggiore.</li>
            <li>Il Gestore può sospendere temporaneamente il servizio per manutenzione programmata, con preavviso ragionevole ove possibile.</li>
            <li>Il Gestore non è responsabile per i contenuti pubblicati dagli utenti nella Community, ma si impegna a rimuovere tempestivamente i contenuti segnalati come illeciti.</li>
          </ul>
        </div>
      ),
    },
    {
      id: '10',
      title: '10. Modifiche ai Termini',
      icon: <RefreshCw className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>
            Il Gestore si riserva il diritto di modificare i presenti Termini di Servizio in qualsiasi momento.
            Le modifiche saranno pubblicate su questa pagina e comunicate tramite la Piattaforma.
          </p>
          <p>
            L'uso continuato della Piattaforma dopo la pubblicazione delle modifiche costituisce accettazione
            delle nuove condizioni.
          </p>
        </div>
      ),
    },
    {
      id: '11',
      title: '11. Legge applicabile e foro competente',
      icon: <Scale className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>
            I presenti Termini di Servizio sono regolati dalla legge italiana. Per qualsiasi controversia derivante
            dall'interpretazione, esecuzione o violazione dei presenti Termini è competente in via esclusiva il
            Foro di Roma.
          </p>
        </div>
      ),
    },
  ];

  return (
    <div className="container-custom py-8 legal-content">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl mb-4">
            <ScrollText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-base font-bold text-gray-900 dark:text-white mb-2">Termini di Servizio</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Condizioni generali di utilizzo della piattaforma studi.unasca.it
          </p>
        </div>

        {/* Sezioni Accordion */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div
              key={section.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-emerald-600 dark:text-emerald-400">{section.icon}</div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{section.title}</h2>
                </div>
                {expandedSections.includes(section.id) ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {expandedSections.includes(section.id) && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700">
                  <div className="pt-4">{section.content}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer aggiornamento */}
        <div className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400">
          <p>Ultimo aggiornamento: 4 aprile 2026</p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
